import React, { useState, useEffect, useContext, useCallback } from "react"; // Added useCallback
import { FaCheckCircle, FaTruck, FaDollarSign, FaClock } from "react-icons/fa";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';
import { UserContext } from '../App';

// Reusable Metric Card as provided in your example
const MetricCard = ({ title, value, icon: Icon, color }) => (
    <div className={`p-6 bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl border-l-4 ${color}`}>
        <div className="flex items-center justify-between">
            <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase">{title}</h3>
                <p className="mt-1 text-4xl font-extrabold text-gray-900">{value}</p>
            </div>
            <div className={`p-4 rounded-full bg-opacity-20 ${color.replace('border-l-', 'bg-')}`}>
                <Icon className={`text-3xl ${color.replace('border-l-', 'text-')}`} />
            </div>
        </div>
    </div>
);

// Custom hook to fetch data for the delivery dashboard
const useFetchDeliveryDashboardData = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useContext(UserContext);

    // Define fetchMyDeliveries inside useEffect to ensure it has the latest state/props if needed
    const fetchMyDeliveries = useCallback(async () => {
        setLoading(true);
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");

        if (!user || !user.userId) {
            setError("Erreur d'authentification: Impossible de trouver votre ID.");
            setLoading(false);
            return;
        }

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const res = await fetch(`${API_URL}/delivery-dashboard-analytics/${user.userId}`, { headers });
            if (!res.ok) throw new Error("Échec de la récupération des analyses du tableau de bord.");

            const dashboardData = await res.json();

            setData({
                ...dashboardData,
                deliveriesOverTime: dashboardData.deliveriesOverTime?.map(item => ({ name: item.date, "Livraisons": item.count })) || [],
                deliveriesByStatus: Object.keys(dashboardData.deliveriesByStatus || {}).map(key => ({
                    name: key, // Keep the key for internal use
                    value: dashboardData.deliveriesByStatus[key]
                })) || []
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]); // Dependencies for fetchMyDeliveries

    useEffect(() => {
        fetchMyDeliveries();
    }, [fetchMyDeliveries]); // Depend on fetchMyDeliveries

    return { data, loading, error };
};


const CHART_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6366f1'];

const DeliveryDashboard = () => {
    const { data, loading, error } = useFetchDeliveryDashboardData();
    const { user } = useContext(UserContext); // Access user here for the greeting

    // French status mapping
    const statusMap = {
        'pending': 'En attente',
        'in_transit': 'En cours de livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    // Helper to get the French status name or a default
    const getFrenchStatus = (statusKey) => statusMap[statusKey] || statusKey;

    if (loading) return (
        <div className="flex items-center justify-center h-screen bg-gray-100 text-gray-700">
            {/* IconLoading component would be here if available */}
            Chargement des données...
        </div>
    );
    if (error) return <div className="flex items-center justify-center h-screen bg-gray-100 text-red-700">Erreur: {error}</div>;
    if (!data) return <div className="flex items-center justify-center h-screen bg-gray-100 text-gray-700">Aucune donnée disponible.</div>;

    // Safely get delivery name, default to "Livreur" if not available
    const deliveryName = user?.lastName ? `${user.firstName} ${user.lastName}` : 'Livreur';

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8">
                    <h1 className="text-4xl font-extrabold text-gray-900">
                        Tableau de bord des Livraisons 🚚
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Bienvenue sur votre tableau de bord, <span className="text-indigo-400">{deliveryName}</span>! Votre aperçu personnel des performances de livraison.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <MetricCard
                        title="Livraisons Terminées"
                        value={data.totalDeliveriesCompleted?.toLocaleString() || 'N/A'}
                        icon={FaCheckCircle}
                        color="border-green-500"
                    />
                    <MetricCard
                        title="En Transit"
                        value={data.totalDeliveriesInTransit?.toLocaleString() || 'N/A'}
                        icon={FaTruck}
                        color="border-yellow-500"
                    />
                    <MetricCard
                        title="Revenu Total"
                        value={`$${(data.totalRevenueEarned || 0).toFixed(2).toLocaleString()}`}
                        icon={FaDollarSign}
                        color="border-indigo-500"
                    />
                    <MetricCard
                        title="Temps Moyen"
                        value={`${(data.averageDeliveryTime || 'N/A')} min`}
                        icon={FaClock}
                        color="border-blue-500"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    <div className="lg:col-span-2 bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg h-[450px]">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Livraisons Terminées par Jour</h3>
                        <ResponsiveContainer width="100%" height="85%">
                            <LineChart data={data.deliveriesOverTime}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                <YAxis tickLine={false} axisLine={false} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="Livraisons" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg h-[450px]">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">État des Livraisons</h3>
                        <ResponsiveContainer width="100%" height="85%">
                            <PieChart>
                                <Pie
                                    data={data.deliveriesByStatus.map(item => ({
                                        ...item,
                                        name: getFrenchStatus(item.name) // Use French status here
                                    }))}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={120}
                                    label
                                >
                                    {data.deliveriesByStatus.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value, name) => [value, getFrenchStatus(name)]} /> {/* French tooltip labels */}
                                <Legend formatter={(value) => getFrenchStatus(value)} /> {/* French legend labels */}
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeliveryDashboard;